import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Divider, InputAdornment, Grid, useMediaQuery,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import RuleIcon from '@mui/icons-material/Rule';
import StorageIcon from '@mui/icons-material/Storage';
import PieChartIcon from '@mui/icons-material/PieChart';
import { alpha } from '@mui/material/styles';
import { smartWarehouseService } from '../services/smartWarehouseService';
import { locationService } from '../services/locationService';
import countingService, { CountTaskDto, CountingStats, DiscrepancyReport, CountCriteria, CountCriteriaType, CriteriaPreview, CriteriaPreviewProduct } from '../services/countingService';
import StatisticalAccuracyPanel from '../shared/StatisticalAccuracyPanel';
import AccountingDataTable from '../../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../../accounting/shared/AccountingDataTable';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS } from '../../accounting/shared/StatsCard';
import AssignWmsPersonnelModal, { type AssignablePersonnel } from '../components/AssignWmsPersonnelModal';
import useWmsCountingSignalR from '../shared/useWmsCountingSignalR';
import WmsNotification, { useWmsNotification } from '../shared/WmsNotification';
import type { CountTaskCancelNotification } from '../services/wmsCountingSignalRService';
import GuideButton from '../../common/guide/GuideButton';

interface Warehouse { id: string; warehouseName: string; }

const COUNT_TYPES = [
  { value: 'Full', label: 'Tam Sayım' },
  { value: 'Zone', label: 'Bölge Sayımı' },
  { value: 'Random', label: 'Rastgele Sayım' },
  { value: 'ProductList', label: 'Özel Liste (Kriter)' },
] as const;

/** Kullanıcıya gösterilen kısa açıklamalar — API seçim mantığıyla uyumlu. */
const COUNT_TYPE_HELP: Record<string, string> = {
  Full: 'Depodaki tüm sayılabilir lokasyonlar (aktif+toplanabilir ve stoğu olan) depo rotasına göre sayılır.',
  Zone: 'Seçtiğiniz koridor ve altındaki tüm raf/lokasyonlar sayılır.',
  Random: 'Sayılabilir lokasyon havuzundan belirttiğiniz adette rastgele seçim yapılır. Konumlar birbirine yakın olmak zorunda değildir; her görevde farklı lokasyonlar çıkabilir. Operatör ekranında liste depo rotasına (SortOrder) göre sıralanır.',
  ProductList: 'Sistem seçtiğiniz kritere göre bir ürün listesi üretir; bu ürünler bulundukları lokasyonlarda sayılır. Operatör her lokasyonda yalnız listedeki ürünleri sayar (ürün odaklı).',
};

/** ProductList (Özel Liste) kriter seçenekleri. */
const CRITERIA_TYPES: { value: CountCriteriaType; label: string; help: string }[] = [
  { value: 'NegativeStock', label: 'Eksi stok', help: 'Bu depoda toplam stoğu sıfırın altına düşmüş ürünler listelenir.' },
  { value: 'LowStock', label: 'Düşük stok (min. altı)', help: 'Depo toplamı ürünün minimum stok seviyesinin altında kalan ürünler listelenir.' },
  { value: 'TopSelling', label: 'Çok satan', help: 'Belirtilen gün aralığında en çok satan/sevk edilen ürünler (Top N) listelenir.' },
  { value: 'NoMovement', label: 'Hareketsiz', help: 'Belirtilen gündür satış/sevk hareketi olmayan ancak stoğu bulunan ürünler listelenir.' },
  { value: 'HighValue', label: 'Yüksek değerli', help: 'Depoda stoğu olan, satış fiyatı en yüksek ürünler (Top N) listelenir.' },
  { value: 'Manual', label: 'Elle seçim (Manuel liste)', help: 'Aşağıdan aradığınız ürünleri elle seçip kendi sayım listenizi oluşturursunuz. Seçilen ürünler depoda stoğu olan lokasyonlarda sayılır.' },
];

const WmsCycleCountManagement: React.FC = () => {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [tasks, setTasks] = useState<CountTaskDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<CountingStats>({
    todayTasks: 0,
    todayLocations: 0,
    pendingApproval: 0,
    inProgressTasks: 0,
    totalPickableLocations: 0,
    occupiedLocations: 0,
    emptyLocations: 0,
    occupancyRatePercent: 0,
    countablePoolSize: 0,
  });

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newCountType, setNewCountType] = useState('Random');
  const [newRandomCount, setNewRandomCount] = useState(10);
  const [creating, setCreating] = useState(false);
  // Bölge (koridor) sayımı için koridor seçimi
  const [aisles, setAisles] = useState<{ id: string; code: string; name: string }[]>([]);
  const [aislesLoading, setAislesLoading] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState('');
  // Özel Liste (Kriter) sayımı için kriter seçimi + önizleme
  const [criteriaType, setCriteriaType] = useState<CountCriteriaType>('NegativeStock');
  const [criteriaTopN, setCriteriaTopN] = useState(20);
  const [criteriaDays, setCriteriaDays] = useState(30);
  const [criteriaNoMovementDays, setCriteriaNoMovementDays] = useState(60);
  const [criteriaPreview, setCriteriaPreview] = useState<CriteriaPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Elle seçim (Manual) kriteri için ürün arama + seçim
  const [manualProducts, setManualProducts] = useState<CriteriaPreviewProduct[]>([]);
  const [manualOptions, setManualOptions] = useState<CriteriaPreviewProduct[]>([]);
  const [manualSearchInput, setManualSearchInput] = useState('');
  const [manualSearchLoading, setManualSearchLoading] = useState(false);

  // Assign dialog
  const [assignTask, setAssignTask] = useState<CountTaskDto | null>(null);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState('');

  // Discrepancy review
  const [reviewTask, setReviewTask] = useState<CountTaskDto | null>(null);
  const [report, setReport] = useState<DiscrepancyReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const { notification, notify, clear } = useWmsNotification();
  const [cancelRequests, setCancelRequests] = useState<CountTaskCancelNotification[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const list = await smartWarehouseService.getAllList();
        setWarehouses(list);
        if (list.length > 0) setSelectedWarehouse((prev) => prev || list[0].id);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!selectedWarehouse) return;
    setLoading(true);
    try {
      const resp = await countingService.getTasks(selectedWarehouse, {
        status: statusFilter || undefined,
        page: page + 1,
        pageSize,
      });
      setTasks(resp.data || []);
      setTotal(resp.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [selectedWarehouse, statusFilter, page, pageSize]);

  const fetchStats = useCallback(async () => {
    if (!selectedWarehouse) return;
    try {
      const data = await countingService.getStats(selectedWarehouse);
      setStats(data);
    } catch {
      setStats({ todayTasks: 0, todayLocations: 0, pendingApproval: 0, inProgressTasks: 0,
        totalPickableLocations: 0, occupiedLocations: 0, emptyLocations: 0,
        occupancyRatePercent: 0, countablePoolSize: 0 });
    }
  }, [selectedWarehouse]);

  useEffect(() => { fetchTasks(); fetchStats(); }, [fetchTasks, fetchStats]);

  const handleCancelRequestReceived = useCallback((payload: CountTaskCancelNotification) => {
    if (selectedWarehouse && payload.warehouseId && payload.warehouseId !== selectedWarehouse) return;

    setCancelRequests((prev) => {
      const exists = prev.some((item) => item.taskId === payload.taskId && item.occurredAt === payload.occurredAt);
      if (exists) return prev;
      return [payload, ...prev].slice(0, 20);
    });

    const locationPart = payload.locationCode ? ` (${payload.locationCode})` : '';
    const byPart = payload.requestedByName ? ` — ${payload.requestedByName}` : '';
    notify({
      type: 'warning',
      message: `Sayım iptal talebi: ${payload.taskNumber}${locationPart}${byPart}. ${payload.reason || ''}`,
      duration: 8000,
      sound: true,
    });
    fetchTasks();
  }, [selectedWarehouse, notify, fetchTasks]);

  useWmsCountingSignalR({
    warehouseId: selectedWarehouse,
    enabled: !!selectedWarehouse,
    onCancelRequested: handleCancelRequestReceived,
    onTaskCancelled: () => fetchTasks(),
  });

  const dismissCancelRequest = useCallback((taskId: string) => {
    setCancelRequests((prev) => prev.filter((item) => item.taskId !== taskId));
  }, []);

  const handleCancelFromRequest = useCallback(async (payload: CountTaskCancelNotification) => {
    try {
      await countingService.cancelTask(payload.taskId, 'Yönetici');
      dismissCancelRequest(payload.taskId);
      fetchTasks();
      fetchStats();
      notify({ type: 'success', message: `${payload.taskNumber} görevi iptal edildi.` });
    } catch {
      notify({ type: 'error', message: 'Görev iptal edilemedi.' });
    }
  }, [dismissCancelRequest, fetchTasks, fetchStats, notify]);

  // Bölge Sayımı için depodaki koridorları (Aisle) yükle.
  useEffect(() => {
    if (!showCreate || !selectedWarehouse) return;
    let cancelled = false;
    setAislesLoading(true);
    (async () => {
      try {
        const res: any = await locationService.getByWarehouse(selectedWarehouse);
        const rows = res?.data?.data ?? res?.data?.Data ?? res?.data ?? [];
        const list = (Array.isArray(rows) ? rows : [])
          .filter((r: any) => String(r?.Type ?? r?.type ?? '').toLowerCase() === 'aisle')
          .map((r: any) => ({
            id: String(r?.Id ?? r?.id ?? ''),
            code: String(r?.Code ?? r?.code ?? ''),
            name: String(r?.Name ?? r?.name ?? ''),
          }))
          .filter((a: { id: string }) => !!a.id);
        if (!cancelled) setAisles(list);
      } catch {
        if (!cancelled) setAisles([]);
      } finally {
        if (!cancelled) setAislesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showCreate, selectedWarehouse]);

  const buildCriteria = useCallback((): CountCriteria => {
    const base: CountCriteria = { Type: criteriaType };
    if (criteriaType === 'TopSelling') { base.TopN = criteriaTopN; base.Days = criteriaDays; }
    if (criteriaType === 'HighValue') { base.TopN = criteriaTopN; }
    if (criteriaType === 'NoMovement') { base.NoMovementDays = criteriaNoMovementDays; }
    if (criteriaType === 'Manual') { base.ProductIds = manualProducts.map(p => p.productId); }
    return base;
  }, [criteriaType, criteriaTopN, criteriaDays, criteriaNoMovementDays, manualProducts]);

  // Kriter değişince önizlemeyi geçersiz kıl (yeniden "Önizle" gereksin).
  useEffect(() => { setCriteriaPreview(null); }, [criteriaType, criteriaTopN, criteriaDays, criteriaNoMovementDays, manualProducts, selectedWarehouse]);

  // Elle seçim: yazıldıkça (debounce) depodaki sayılabilir ürünleri ara.
  useEffect(() => {
    if (criteriaType !== 'Manual' || !selectedWarehouse) return;
    const term = manualSearchInput.trim();
    let active = true;
    setManualSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await countingService.searchProducts(selectedWarehouse, term);
        if (active) setManualOptions(data);
      } catch {
        if (active) setManualOptions([]);
      } finally {
        if (active) setManualSearchLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(handle); };
  }, [manualSearchInput, criteriaType, selectedWarehouse]);

  const handlePreview = useCallback(async () => {
    if (!selectedWarehouse) return;
    setPreviewLoading(true);
    try {
      const data = await countingService.previewCriteria(selectedWarehouse, buildCriteria());
      setCriteriaPreview(data);
    } catch {
      setCriteriaPreview(null);
      notify({ type: 'error', message: 'Önizleme alınamadı.' });
    }
    setPreviewLoading(false);
  }, [selectedWarehouse, buildCriteria, notify]);

  const resetCreateForm = useCallback(() => {
    setShowCreate(false);
    setNewTaskName('');
    setSelectedZoneId('');
    setCriteriaPreview(null);
    setManualProducts([]);
    setManualOptions([]);
    setManualSearchInput('');
  }, []);

  const handleCreate = useCallback(async () => {
    setCreating(true);
    try {
      await countingService.createTask({
        WarehouseId: selectedWarehouse,
        TaskName: newTaskName,
        CountType: newCountType,
        ZoneId: newCountType === 'Zone' ? (selectedZoneId || undefined) : undefined,
        RandomCount: newCountType === 'Random' ? newRandomCount : undefined,
        Criteria: newCountType === 'ProductList' ? buildCriteria() : undefined,
      });
      resetCreateForm();
      fetchTasks();
    } catch { /* ignore */ }
    setCreating(false);
  }, [selectedWarehouse, newTaskName, newCountType, selectedZoneId, newRandomCount, buildCriteria, resetCreateForm, fetchTasks]);

  const handleAssign = useCallback(async (personnel: AssignablePersonnel) => {
    if (!assignTask) return;
    setAssignSaving(true);
    setAssignError('');
    try {
      try {
        await countingService.assignPersonnelToTask(assignTask.id, personnel.id, personnel.name);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 404 || status === 405) {
          // Eski endpoint'e fallback: numeric cashierId gerekir
          if (!personnel.cashierId) {
            throw new Error(
              'Bu personelin Kasa ID bilgisi eksik. Personel kaydında "cashierId" tanımlı olmalı.'
            );
          }
          await countingService.assignTask(assignTask.id, personnel.cashierId, personnel.name);
        } else {
          throw err;
        }
      }
      setAssignTask(null);
      fetchTasks();
    } catch (err: any) {
      const apiMessage = err?.response?.data?.Message || err?.response?.data?.message;
      setAssignError(apiMessage || err?.message || 'Atama işlemi başarısız.');
    } finally {
      setAssignSaving(false);
    }
  }, [assignTask, fetchTasks]);

  const handleCancel = useCallback(async (task: CountTaskDto) => {
    try {
      await countingService.cancelTask(task.id);
      fetchTasks();
    } catch { /* ignore */ }
  }, [fetchTasks]);

  const handleViewReport = useCallback(async (task: CountTaskDto) => {
    setReviewTask(task);
    setReportLoading(true);
    try {
      const data = await countingService.getDiscrepancyReport(task.id);
      setReport(data);
    } catch { setReport(null); }
    setReportLoading(false);
  }, []);

  const handleApprove = useCallback(async () => {
    if (!reviewTask) return;
    try {
      await countingService.approveAdjustments(reviewTask.id, 'Yönetici');
      setReviewTask(null);
      setReport(null);
      fetchTasks();
    } catch { /* ignore */ }
  }, [reviewTask, fetchTasks]);

  const handleReject = useCallback(async () => {
    if (!reviewTask) return;
    try {
      await countingService.rejectAdjustments(reviewTask.id);
      setReviewTask(null);
      setReport(null);
      fetchTasks();
    } catch { /* ignore */ }
  }, [reviewTask, fetchTasks]);

  const statusColor = (s: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
    if (s === 'Created') return 'default';
    if (s === 'InProgress') return 'warning';
    if (s === 'Completed') return 'info';
    if (s === 'Approved') return 'success';
    if (s === 'Cancelled') return 'error';
    return 'default';
  };

  const statusLabel = (s: string): string => {
    if (s === 'Created') return 'Yeni';
    if (s === 'InProgress') return 'Devam';
    if (s === 'Completed') return 'Tamamlandı';
    if (s === 'Approved') return 'Onaylandı';
    if (s === 'Cancelled') return 'İptal';
    return s;
  };

  const filteredTasks = tasks.filter((t) => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    return (
      (t.taskNumber || '').toLowerCase().includes(q) ||
      (t.taskName || '').toLowerCase().includes(q) ||
      (t.assignedCashierName || '').toLowerCase().includes(q) ||
      (t.assignedPersonnelName || '').toLowerCase().includes(q)
    );
  });

  const columns: DataTableColumn<CountTaskDto>[] = [
    {
      id: 'rowNo',
      label: 'Sıra',
      width: '70px',
      align: 'center',
      render: (_task, index) => (
        <Typography variant="body2" fontWeight={600}>
          {page * pageSize + index + 1}
        </Typography>
      ),
    },
    { id: 'taskNumber', label: 'Görev', field: 'taskNumber', sortable: true, width: '155px' },
    { id: 'taskName', label: 'Ad', field: 'taskName', sortable: true, width: '190px' },
    {
      id: 'countType',
      label: 'Tip',
      width: '130px',
      render: (t) => {
        const typeLabel = COUNT_TYPES.find(ct => ct.value === t.countType)?.label || t.countType;
        const randomHint = t.countType === 'Random' && t.randomCount ? ` (${t.randomCount})` : '';
        return (
          <Chip label={`${typeLabel}${randomHint}`} size="small" variant="outlined" />
        );
      },
    },
    {
      id: 'locations',
      label: 'Lokasyon',
      width: '110px',
      align: 'center',
      render: (t) => <Typography variant="body2" fontWeight={600}>{t.countedLocations}/{t.totalLocations}</Typography>,
    },
    {
      id: 'assignedCashierName',
      label: 'Görevli',
      field: 'assignedCashierName',
      sortable: true,
      width: '150px',
      render: (t) => t.assignedPersonnelName || t.assignedCashierName || '-',
    },
    {
      id: 'status',
      label: 'Durum',
      width: '120px',
      align: 'center',
      render: (t) => (
        <Chip
          label={statusLabel(t.status)}
          size="small"
          color={statusColor(t.status)}
          sx={{ minWidth: 94, '& .MuiChip-label': { textAlign: 'center', width: '100%' } }}
        />
      ),
    },
    {
      id: 'discrepancy',
      label: 'Fark',
      width: '90px',
      align: 'center',
      render: (t) => (
        t.discrepancyCount > 0 ? (
          <Chip label={t.discrepancyCount} size="small" color="error" />
        ) : t.status === 'Completed' || t.status === 'Approved' ? (
          <Chip label="0" size="small" color="success" />
        ) : '-'
      ),
    },
    {
      id: 'actions',
      label: 'İşlem',
      width: '130px',
      align: 'center',
      render: (t) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          {(t.status === 'Completed') && (
            <IconButton size="small" color="primary" title="Fark Raporu" onClick={() => handleViewReport(t)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          )}
          {t.status === 'Created' && (
            <IconButton size="small" color="info" title="Görevli Ata" onClick={() => setAssignTask(t)}>
              <PersonAddIcon fontSize="small" />
            </IconButton>
          )}
          {(t.status === 'Created' || t.status === 'InProgress') && (
            <IconButton size="small" color="error" title="İptal" onClick={() => handleCancel(t)}>
              <CancelIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WmsNotification notification={notification} onClose={clear} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Stok Sayım Yönetimi</Typography>
          <GuideButton guideKey="wms.cycleCount" />
        </Box>

        <FormControl
          size="small"
          sx={{
            minWidth: 230,
            bgcolor: 'background.paper',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        >
          <InputLabel>Depo</InputLabel>
          <Select
            value={selectedWarehouse}
            onChange={e => { setSelectedWarehouse(e.target.value); setPage(0); }}
            label="Depo"
            startAdornment={<WarehouseIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />}
          >
            {warehouses.map(w => <MenuItem key={w.id} value={w.id}>{w.warehouseName}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {cancelRequests.length > 0 && (
        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.main',
            bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'warning.dark' }}>
            İptal Bildirgeleri ({cancelRequests.length})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {cancelRequests.map((req) => (
              <Box
                key={`${req.taskId}-${req.occurredAt}`}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {req.taskNumber} — {req.taskName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {req.requestedByName || 'Operatör'}
                    {req.locationCode ? ` • Lokasyon: ${req.locationCode}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    {req.reason}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                  <Button size="small" onClick={() => dismissCancelRequest(req.taskId)}>
                    Kapat
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={() => handleCancelFromRequest(req)}
                  >
                    Görevi İptal Et
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <StatsCardsContainer loading={loading && total === 0}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem icon={<Inventory2Icon />} color={STATS_COLORS.blue} title={String(total)} subtitle="Toplam Görev" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem icon={<EventNoteIcon />} color={STATS_COLORS.purple} title={String(stats.todayTasks)} subtitle="Bugün Görev" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem icon={<HourglassTopIcon />} color={STATS_COLORS.orange} title={String(stats.inProgressTasks)} subtitle="Devam Eden" />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem icon={<RuleIcon />} color={STATS_COLORS.teal} title={String(stats.pendingApproval)} subtitle="Onay Bekleyen" />
        </Grid>
      </StatsCardsContainer>

      <StatsCardsContainer loading={loading && total === 0}>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem
            icon={<PieChartIcon />}
            color={STATS_COLORS.green}
            title={`%${stats.occupancyRatePercent ?? 0}`}
            subtitle="Depo Doluluk Oranı"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem
            icon={<StorageIcon />}
            color={STATS_COLORS.blue}
            title={`${stats.occupiedLocations ?? 0}/${stats.totalPickableLocations ?? 0}`}
            subtitle="Dolu / Toplanabilir Lokasyon"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem
            icon={<WarehouseIcon />}
            color={STATS_COLORS.grey}
            title={String(stats.emptyLocations ?? 0)}
            subtitle="Boş Lokasyon"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatsCardItem
            icon={<Inventory2Icon />}
            color={STATS_COLORS.purple}
            title={String(stats.countablePoolSize ?? 0)}
            subtitle="Rastgele Sayım Havuzu"
          />
        </Grid>
      </StatsCardsContainer>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 150,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
          >
            <InputLabel>Durum</InputLabel>
            <Select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} label="Durum">
              <MenuItem value="">Tümü</MenuItem>
              <MenuItem value="Created">Yeni</MenuItem>
              <MenuItem value="InProgress">Devam Eden</MenuItem>
              <MenuItem value="Completed">Tamamlanan</MenuItem>
              <MenuItem value="Approved">Onaylanan</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            placeholder="Görev no, görev adı, görevli..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              minWidth: { xs: '100%', sm: 280 },
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreate(true)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Sayım Görevi Oluştur
          </Button>
          <IconButton onClick={() => { fetchTasks(); fetchStats(); }} size="small"><RefreshIcon /></IconButton>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          overflow: 'hidden',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.15),
          bgcolor: 'background.paper',
        }}
      >
        {isMobile ? (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Görev bulunamadı</Typography>
            ) : (
              <>
                {filteredTasks.map((t, index) => (
                  <Paper key={t.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {page * pageSize + index + 1}. {t.taskNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {t.taskName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={COUNT_TYPES.find((ct) => ct.value === t.countType)?.label || t.countType}
                      />
                      <Chip
                        size="small"
                        color={statusColor(t.status)}
                        label={statusLabel(t.status)}
                        sx={{ minWidth: 94, '& .MuiChip-label': { textAlign: 'center', width: '100%' } }}
                      />
                      <Chip size="small" variant="outlined" label={`${t.countedLocations}/${t.totalLocations} lokasyon`} />
                      {t.discrepancyCount > 0 && <Chip size="small" color="error" label={`Fark: ${t.discrepancyCount}`} />}
                    </Box>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                      Görevli: {t.assignedPersonnelName || t.assignedCashierName || '-'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      {t.status === 'Completed' && (
                        <IconButton size="small" color="primary" title="Fark Raporu" onClick={() => handleViewReport(t)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {t.status === 'Created' && (
                        <IconButton size="small" color="info" title="Görevli Ata" onClick={() => setAssignTask(t)}>
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      )}
                      {(t.status === 'Created' || t.status === 'InProgress') && (
                        <IconButton size="small" color="error" title="İptal" onClick={() => handleCancel(t)}>
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={page === 0}
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                  >
                    Önceki
                  </Button>
                  <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                    Sayfa {page + 1} / {Math.max(1, Math.ceil(total / pageSize))}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={(page + 1) * pageSize >= total}
                    onClick={() => setPage((prev) => prev + 1)}
                  >
                    Sonraki
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : (
          <AccountingDataTable<CountTaskDto>
            columns={columns}
            rows={filteredTasks}
            getRowId={(row) => row.id}
            loading={loading}
            serverSidePagination={true}
            totalCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            }}
            emptyMessage="Görev bulunamadı"
            ariaLabel="WMS stok sayım görevleri tablosu"
            columnResizeStorageKey="wms-cycle-count-management"
          />
        )}
      </Paper>

      {/* Create Dialog */}
      <Dialog open={showCreate} onClose={resetCreateForm} maxWidth="sm" fullWidth>
        <DialogTitle>Sayım Görevi Oluştur</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Görev Adı" value={newTaskName} onChange={e => setNewTaskName(e.target.value)} fullWidth size="small" required />
          <FormControl size="small" fullWidth>
            <InputLabel>Sayım Tipi</InputLabel>
            <Select
              value={newCountType}
              onChange={e => { setNewCountType(e.target.value); setSelectedZoneId(''); setCriteriaPreview(null); }}
              label="Sayım Tipi"
            >
              {COUNT_TYPES.map(ct => <MenuItem key={ct.value} value={ct.value}>{ct.label}</MenuItem>)}
            </Select>
          </FormControl>
          {COUNT_TYPE_HELP[newCountType] && (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {COUNT_TYPE_HELP[newCountType]}
            </Typography>
          )}
          {newCountType === 'Zone' && (
            <FormControl size="small" fullWidth required>
              <InputLabel>Koridor</InputLabel>
              <Select
                value={selectedZoneId}
                onChange={e => setSelectedZoneId(e.target.value)}
                label="Koridor"
              >
                {aislesLoading && <MenuItem value="" disabled>Yükleniyor...</MenuItem>}
                {!aislesLoading && aisles.length === 0 && (
                  <MenuItem value="" disabled>Bu depoda koridor bulunamadı</MenuItem>
                )}
                {aisles.map(a => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.code || a.name}{a.name && a.code && a.name !== a.code ? ` — ${a.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {newCountType === 'Random' && (
            <TextField
              label="Lokasyon Sayısı"
              type="number"
              value={newRandomCount}
              onChange={e => setNewRandomCount(Math.max(1, parseInt(e.target.value) || 1))}
              fullWidth
              size="small"
              helperText={
                stats.countablePoolSize
                  ? `Havuzda ${stats.countablePoolSize} sayılabilir lokasyon var. Sistem eşit olasılıkla ${Math.min(newRandomCount, stats.countablePoolSize)} adres seçer. 10/10 eşleşme → %90+ doğruluk; 4+ fark → alarm.`
                  : 'Havuzdaki lokasyon sayısından fazla girilirse tümü seçilir.'
              }
              InputProps={{ inputProps: { min: 1, max: 500 } }}
            />
          )}
          {newCountType === 'ProductList' && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Kriter</InputLabel>
                <Select
                  value={criteriaType}
                  onChange={e => setCriteriaType(e.target.value as CountCriteriaType)}
                  label="Kriter"
                >
                  {CRITERIA_TYPES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {CRITERIA_TYPES.find(c => c.value === criteriaType)?.help}
              </Typography>

              {(criteriaType === 'TopSelling' || criteriaType === 'HighValue') && (
                <TextField
                  label="Ürün adedi (Top N)"
                  type="number"
                  value={criteriaTopN}
                  onChange={e => setCriteriaTopN(Math.max(1, parseInt(e.target.value) || 1))}
                  fullWidth
                  size="small"
                  InputProps={{ inputProps: { min: 1, max: 500 } }}
                />
              )}
              {criteriaType === 'TopSelling' && (
                <TextField
                  label="Gün aralığı (son N gün)"
                  type="number"
                  value={criteriaDays}
                  onChange={e => setCriteriaDays(Math.max(1, parseInt(e.target.value) || 1))}
                  fullWidth
                  size="small"
                  InputProps={{ inputProps: { min: 1, max: 3650 } }}
                />
              )}
              {criteriaType === 'NoMovement' && (
                <TextField
                  label="Hareketsizlik eşiği (gün)"
                  type="number"
                  value={criteriaNoMovementDays}
                  onChange={e => setCriteriaNoMovementDays(Math.max(1, parseInt(e.target.value) || 1))}
                  fullWidth
                  size="small"
                  InputProps={{ inputProps: { min: 1, max: 3650 } }}
                />
              )}

              {criteriaType === 'Manual' && (
                <>
                  <Autocomplete
                    multiple
                    size="small"
                    fullWidth
                    disableCloseOnSelect
                    options={manualOptions}
                    value={manualProducts}
                    filterOptions={x => x}
                    loading={manualSearchLoading}
                    loadingText="Aranıyor..."
                    noOptionsText={manualSearchInput.trim() ? 'Eşleşen ürün yok' : 'Aramak için ürün adı, barkod veya stok kodu yazın'}
                    getOptionLabel={o => o.productName || o.stockCode || o.barcode || o.productId}
                    isOptionEqualToValue={(o, v) => o.productId === v.productId}
                    onChange={(_e, val) => setManualProducts(val)}
                    inputValue={manualSearchInput}
                    onInputChange={(_e, val, reason) => { if (reason === 'input') setManualSearchInput(val); }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.productId}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2">{option.productName || option.stockCode || option.barcode || option.productId}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[option.stockCode, option.barcode].filter(Boolean).join(' • ')}
                            {` — Stok: ${option.warehouseQuantity} · ${option.locationCount} lok.`}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Ürün ara ve ekle"
                        placeholder="Ad, barkod veya stok kodu"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {manualSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {manualProducts.length > 0
                      ? `${manualProducts.length} ürün seçildi. Sayılacak lokasyonları görmek için "Önizle"ye basın.`
                      : 'Yalnızca bu depoda stoğu (lokasyonu) olan ürünler listelenir.'}
                  </Typography>
                </>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outlined" size="small" onClick={handlePreview} disabled={previewLoading || !selectedWarehouse || (criteriaType === 'Manual' && manualProducts.length === 0)}>
                  {previewLoading ? 'Hesaplanıyor...' : 'Önizle'}
                </Button>
                {criteriaPreview && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" color="primary" label={`${criteriaPreview.productCount} ürün`} />
                    <Chip size="small" color="secondary" label={`${criteriaPreview.locationCount} lokasyon`} />
                  </Box>
                )}
              </Box>

              {previewLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
              )}
              {criteriaPreview && criteriaPreview.products.length > 0 && (
                <Paper variant="outlined" sx={{ maxHeight: 240, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Ürün</TableCell>
                        <TableCell align="center">Depo Stok</TableCell>
                        <TableCell align="center">Lok.</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {criteriaPreview.products.map(p => (
                        <TableRow key={p.productId}>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                              {p.productName || p.stockCode || p.barcode || p.productId}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{p.warehouseQuantity}</TableCell>
                          <TableCell align="center">{p.locationCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
              {criteriaPreview && criteriaPreview.products.length === 0 && (
                <Typography variant="body2" color="warning.main">
                  Bu kriterle eşleşen ürün bulunamadı. Görev oluşturulamaz.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCreateForm}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={
              creating || !newTaskName
              || (newCountType === 'Zone' && !selectedZoneId)
              || (newCountType === 'ProductList' && (!criteriaPreview || criteriaPreview.locationCount === 0))
            }
          >
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Dialog */}
      <AssignWmsPersonnelModal
        open={!!assignTask}
        title={`Görevli Ata${assignTask?.taskNumber ? ` - ${assignTask.taskNumber}` : ''}`}
        subtitle={assignTask?.taskName ? `Görev: ${assignTask.taskName}` : undefined}
        warehouseId={selectedWarehouse}
        screenType="cyclecount"
        currentPersonnelId={assignTask?.assignedPersonnelId}
        currentPersonnelName={assignTask?.assignedPersonnelName || assignTask?.assignedCashierName}
        saving={assignSaving}
        externalError={assignError}
        onClose={() => { setAssignTask(null); setAssignError(''); }}
        onSubmit={handleAssign}
        emptyMessage="Bu depoda Stok Sayım ekranı tanımlanmamış. Önce 'Stok Sayım Ayarları'ndan ekran ekleyip personel atayın."
      />

      {/* Discrepancy Report Dialog */}
      <Dialog open={!!reviewTask} onClose={() => { setReviewTask(null); setReport(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Fark Raporu - {reviewTask?.taskNumber}</DialogTitle>
        <DialogContent>
          {reportLoading ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
          ) : report ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {report.statisticalAccuracy && (
                <StatisticalAccuracyPanel data={report.statisticalAccuracy} />
              )}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Typography>Toplam: <strong>{report.totalLocations}</strong></Typography>
                <Typography>
                  Lokasyon farkı: <strong style={{ color: '#ef5350' }}>
                    {report.locationDiscrepancyCount ?? report.discrepancies.length}
                  </strong>
                </Typography>
                <Typography>
                  Lokasyon eşleşme: <strong style={{ color: '#4caf50' }}>
                    {report.locationMatchCount ?? '-'}
                  </strong>
                </Typography>
                <Typography>Ürün satırı farkı: <strong>{report.discrepancyCount}</strong></Typography>
              </Box>

              {report.discrepancies.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>FARKLAR</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Lokasyon</TableCell>
                        <TableCell>Ürün</TableCell>
                        <TableCell align="center">Sistem</TableCell>
                        <TableCell align="center">Sayım</TableCell>
                        <TableCell align="center">Fark</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.discrepancies.map(d => (
                        <TableRow key={d.resultId} sx={{ bgcolor: 'rgba(239,83,80,0.05)' }}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.locationCode}</TableCell>
                          <TableCell>{d.isEmpty ? '(boş)' : (d.productName || '-')}</TableCell>
                          <TableCell align="center">{d.systemQuantity}</TableCell>
                          <TableCell align="center">{d.countedQuantity}</TableCell>
                          <TableCell align="center">
                            <Chip label={`${d.difference > 0 ? '+' : ''}${d.difference}`} size="small"
                              color={d.difference > 0 ? 'info' : 'error'} sx={{ fontWeight: 700 }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          ) : (
            <Typography color="error">Rapor yüklenemedi.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {report && reviewTask?.status === 'Completed' && (
            <>
              <Button startIcon={<DoNotDisturbIcon />} color="warning" onClick={handleReject}>
                Reddet (Tekrar Say)
              </Button>
              <Button startIcon={<CheckCircleIcon />} variant="contained" color="success" onClick={handleApprove}>
                Farkları Onayla (Stok Düzelt)
              </Button>
            </>
          )}
          <Button onClick={() => { setReviewTask(null); setReport(null); }}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmsCycleCountManagement;
